import { Button } from '@components/common';
import { motion, useReducedMotion } from 'framer-motion';
import { Clock, User, Utensils } from 'lucide-react';
import React from 'react';

import { KDS_DELAYED_THRESHOLD_MINUTES, KdsTicket } from '@/renderer/types/kds';
import { KdsColumnConfig } from './kdsColumns';

interface KdsTicketCardProps {
  ticket: KdsTicket;
  column: KdsColumnConfig;
  now: number;
  isTransitioning: boolean;
  onAdvance: (
    ticketId: string,
    toStatus: KdsColumnConfig['nextStatus'],
  ) => void;
}

export const KdsTicketCard: React.FC<KdsTicketCardProps> = ({
  ticket,
  column,
  now,
  isTransitioning,
  onAdvance,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const elapsedMinutes = Math.max(
    0,
    Math.floor((now - new Date(ticket.created_at).getTime()) / 60_000),
  );
  const isDelayed = elapsedMinutes >= KDS_DELAYED_THRESHOLD_MINUTES;

  return (
    <motion.div
      layoutId={`ticket-${ticket.id}`}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { type: 'spring', stiffness: 500, damping: 40 }
      }
      className={`flex flex-col gap-3 rounded-2xl border bg-white p-4 shadow-sm ${
        isDelayed ? 'border-red-300' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-black">
              #{ticket.bill_number}
            </span>
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              {ticket.mode}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-700">
            {ticket.table_name && (
              <span className="flex items-center gap-1">
                <Utensils size={14} /> {ticket.table_name}
              </span>
            )}
            {ticket.waiter_name && (
              <span className="flex items-center gap-1">
                <User size={14} /> {ticket.waiter_name}
              </span>
            )}
          </div>
        </div>
        <span
          className={`flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-sm font-bold ${
            isDelayed ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-700'
          }`}
        >
          <Clock size={14} />
          {elapsedMinutes}m
        </span>
      </div>

      <ul className="space-y-2 border-y border-gray-100 py-3">
        {ticket.items.map((item) => (
          <li key={item.id} className="text-lg">
            <div className="flex gap-2">
              <span className="min-w-[2rem] font-bold text-black">
                {item.quantity}x
              </span>
              <span className="font-medium text-gray-900">{item.name}</span>
            </div>
            {item.notes && (
              <p className="ml-8 text-sm font-medium italic text-gray-700">
                {item.notes}
              </p>
            )}
          </li>
        ))}
      </ul>

      <Button
        onClick={() => onAdvance(ticket.id, column.nextStatus)}
        isLoading={isTransitioning}
        size="lg"
        fullWidth
      >
        {column.actionLabel}
      </Button>
    </motion.div>
  );
};
